// frontend/src/components/RoutesManagement.jsx

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Edit, Delete, AlertCircle, Calculator } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import axios from '../axiosConfig';
import MapWithTooltips from './MapWithTooltips';
import MapControls from './MapControls';
import { useTranslation } from 'react-i18next';
import { getGlobalThreshold } from '../utils/preferences';
import { usePreferences } from '../contexts/PreferenceContext';
import { useDateTimePreferences } from '../hooks/useDateTimePreferences';
import L from 'leaflet';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import { geocodeAddress } from '../utils/geocodingApi';
import '../index.css';
import { toast } from 'react-toastify';
import DeleteConfirmDialog from './dialogs/DeleteConfirmDialog';
import StatusChangeConfirmDialog from './dialogs/StatusChangeConfirmDialog';
import SegmentMatrixModal from './dialogs/SegmentMatrixModal';
import segmentMatrixService from '../services/segmentMatrixService';
import shortcutSegmentService from '../services/shortcutSegmentService';
import translationService from '../services/translationService';
import MultiLanguageField from './common/MultiLanguageField';
import { CircleMarker, Polyline, Tooltip } from 'react-leaflet';
import { useTheme } from '../contexts/ThemeContext';
import { ThemedButton, ThemedCard, ThemedInput } from './themed';
import WaypointFormCard from './WaypointFormCard';
import { formatDistanceKilometers, formatDistanceMeters, getZeroDistanceLabel } from '../utils/distanceUtils';

let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

const SEGMENT_COLORS = [
    '#F97316',
    '#EF4444',
    '#8B5CF6',
    '#EC4899',
    '#14B8A6',
    '#F59E0B',
    '#6366F1',
    '#10B981'
];
const BREADCRUMB_MAX_POINTS = 200;

const haversineDistanceMeters = (a, b) => {
    const toRadians = (value) => (value * Math.PI) / 180;
    const earthRadiusM = 6371000;
    const lat1 = toRadians(a.lat);
    const lat2 = toRadians(b.lat);
    const dLat = lat2 - lat1;
    const dLng = toRadians(b.lng - a.lng);

    const h = Math.sin(dLat / 2) ** 2
        + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return 2 * earthRadiusM * Math.asin(Math.sqrt(h));
};

const parseWaypointCoord = (waypoint) => {
    if (!waypoint) return null;
    const lat = Number(waypoint.latitude);
    const lng = Number(waypoint.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng };
};

const parsePolylinePoint = (point) => {
    if (!Array.isArray(point) || point.length < 2) return null;
    const lat = Number(point[0]);
    const lng = Number(point[1]);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng };
};

const formatIsoTimestamp = (value) => {
    if (!value) return 'n/a';
    const timestamp = new Date(value);
    if (Number.isNaN(timestamp.getTime())) return String(value);
    return timestamp.toISOString();
};

const formatCoord = (value) => (
    Number.isFinite(value) ? value.toFixed(6) : 'n/a'
);
const RouteManagement = ({ currentEntity }) => {
    const mapRef = useRef(null);
    const { entitySettings } = usePreferences();
    const { distanceUnit } = useDateTimePreferences();
    const { background, text, components, cn } = useTheme();
    const [routes, setRoutes] = useState([]);
    const [selectedRoute, setSelectedRoute] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [showEditForm, setShowEditForm] = useState(null); // Separate state for edit form
    const [newRoute, setNewRoute] = useState({
        name: '',
        status: 'active',
        description: '',
    });

    const [advancedMode, setAdvancedMode] = useState(false);
    
    const [stops, setStops] = useState([]);
    const [showDefaultMarker, setShowDefaultMarker] = useState(false);
    const [, setMapZoom] = useState(13);
    const [, setIsLoading] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [showStatusChangeDialog, setShowStatusChangeDialog] = useState(false);
    const [routeToUpdate, setRouteToUpdate] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [routeToDelete, setRouteToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [entityAddress, setEntityAddress] = useState(null);
    const [, setEditingRouteId] = useState(null);
    const { t, i18n } = useTranslation();
    const [pickingMode, setPickingMode] = useState(null); 
    const [currentStopIndex, setCurrentStopIndex] = useState(null);
    const [filteredRoutes, setFilteredRoutes] = useState([]);
    const [searchTerm, setSearchTerm] = useState(''); // Search filter
    const globalThreshold = getGlobalThreshold();
    const entityChangeRef = useRef(false);
    const [showInactive, setShowInactive] = useState(false);
    const [hasInactiveRoutes, setHasInactiveRoutes] = useState(false);
    const [showTooltips, setShowTooltips] = useState(true);
    const [routeReferenceLocations, setRouteReferenceLocations] = useState(null);
    const [segmentPolylines, setSegmentPolylines] = useState([]);
    const [segmentPolylinesArchivedType, setSegmentPolylinesArchivedType] = useState(null);
    const [availableRouteTypes, setAvailableRouteTypes] = useState([]);
    const [highlightedSegmentKey, setHighlightedSegmentKey] = useState(null);
    const [hasRouteReferenceLocations, setHasRouteReferenceLocations] = useState(false);
    const [showSegmentMatrixModal, setShowSegmentMatrixModal] = useState(false);
    const [segmentMatrixRoute, setSegmentMatrixRoute] = useState(null);
    const [routeSegments, setRouteSegments] = useState({}); // { routeId: [segments] }

    const getRouteDisplayName = useCallback((route) => (
        route?.displayName || route?.name || ''
    ), []);

    const getWaypointDisplayName = useCallback((waypoint) => (
        waypoint?.displayName || waypoint?.name || ''
    ), []);

    const getWaypointDisplayAddress = useCallback((waypoint) => (
        waypoint?.displayAddress || waypoint?.address || ''
    ), []);

    // Boarding image upload states
    const [boardingImages, setBoardingImages] = useState({}); // { waypointIndex: File }
    const [boardingImagePreviews, setBoardingImagePreviews] = useState({}); // { waypointIndex: dataUrl }
    const [uploadingImages, setUploadingImages] = useState({}); // { waypointIndex: boolean }

    const mapControlRef = useRef(null);
    // Check if there are hidden waypoints in the selected route    
    const hasHiddenWaypoints = selectedRoute?.waypoints?.some(
        waypoint => waypoint.point_type !== 'stop'
      );      
    // Disable certain actions in non-advanced mode if hidden waypoints exist
    const restrictEditingInNormalMode = !advancedMode && hasHiddenWaypoints;

    // center to entity address for initial loading
    const [mapCenter, setMapCenter] = useState(() => {
        const storedLat = currentEntity?.latitude;
        const storedLng = currentEntity?.longitude;
        
        const lat = parseFloat(storedLat);
        const lng = parseFloat(storedLng);
        
        if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
            return [lat, lng];
        }
        
        // Default coordinates if stored values are invalid or not present
        return [43.7921, -79.3552]; // 3300 Don Mills Road
    });

    // center to entity address when seleting a new entity
    useEffect(() => {
        if (currentEntity) {
            const lat = parseFloat(currentEntity.latitude);
            const lng = parseFloat(currentEntity.longitude);
            
            if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
                entityChangeRef.current = true;
                setMapCenter([lat, lng]);
                setMapZoom(13); // Or whatever default zoom level you prefer
                setShowDefaultMarker(true);
            } else {
                // If the new entity doesn't have valid coordinates, use the default
                entityChangeRef.current = true;
                setMapCenter([43.7921, -79.3552]); // Default coordinates (3300 Don Mills Road)
                setMapZoom(13);
                setShowDefaultMarker(false); // Optionally hide the marker if there's no specific location
            }
            
            setSelectedRoute(null); // Clear any selected route
            setEntityAddress(currentEntity.address || `${currentEntity.entityName} Address`);
        } else {
            // If there's no currentEntity, use the default location
            entityChangeRef.current = true;
            setMapCenter([43.7921, -79.3552]);
            setMapZoom(13);
            setShowDefaultMarker(false);
            setEntityAddress('');
        }
    }, [currentEntity]);

    // Clear segment overlay when switching entity context.
    useEffect(() => {
        setSegmentPolylines([]);
        setSegmentPolylinesArchivedType(null);
        setAvailableRouteTypes([]);
        setHighlightedSegmentKey(null);
    }, [currentEntity?.entityId]);

    // Add an effect to check for inactive routes
    useEffect(() => {
        const inactiveExists = routes.some(route => route.status === 'inactive');
        setHasInactiveRoutes(inactiveExists);
    }, [routes]);

    
    useEffect(() => {
        // Initialize map control after component mounts
        if (mapControlRef.current === null) {
            mapControlRef.current = { centerMap: () => console.log('Default centerMap - will be replaced') };
        }
    }, []);

    // Map cursor change when picking location
    useEffect(() => {
        
        const map = mapRef.current;
        if (map) {

            console.log('Map instance in useEffect:', map);
            console.log('Current picking mode:', pickingMode);

            const mapElement = map.getContainer();
            if (pickingMode) {
                // Change to crosshair when picking location
                console.log(`Picking mode (${pickingMode}) after setting cursor`);

                mapElement.classList.add('cursor-crosshair');
                mapElement.classList.remove('cursor-grab');
                // Disable map dragging and box zoom to avoid interference
                map.dragging.disable();
                map.boxZoom.disable();
                map.doubleClickZoom.disable();
                map.scrollWheelZoom.disable();
                map.keyboard.disable();
            } else {
                // Default cursor for normal interaction
                console.log('Normal mode after setting cursor');

                mapElement.classList.add('cursor-grab');
                mapElement.classList.remove('cursor-crosshair');
                // Re-enable map interactions
                map.dragging.enable();
                map.boxZoom.enable();
                map.doubleClickZoom.enable();
                map.scrollWheelZoom.enable();
                map.keyboard.enable();
            }
        }
    }, [mapRef, pickingMode]);

    // fetch route reference locations
    const fetchRouteReferenceLocations = async (routeId) => {
        if (!routeId || !currentEntity?.entityId) {
          console.log('Missing required IDs for route reference locations');
          return;
        }
        
        try {
          const response = await axios.get(`/api/trips/${currentEntity.entityId}/${routeId}/route-reference-locations`);
          
          if (response.data.success && response.data.data.length > 0) {
            const points = response.data.data.map(loc => [
              parseFloat(loc.latitude),
              parseFloat(loc.longitude)
            ]);
            setRouteReferenceLocations(points);
            setHasRouteReferenceLocations(true);    
          } else {
            setRouteReferenceLocations(null);
            setHasRouteReferenceLocations(false);
          }
        } catch (error) {
          console.error('Error fetching route reference locations:', error);
          setRouteReferenceLocations(null);
          setHasRouteReferenceLocations(false);
        }
      };

    const fetchSegmentPolylines = useCallback(async (routeId, archivedType = null) => {
        if (!routeId || !currentEntity?.entityId) {
            return { segments: [], archivedType: null, availableTypes: [] };
        }

        try {
            const response = await shortcutSegmentService.getSegmentPolylines(
                currentEntity.entityId,
                routeId,
                archivedType
            );

            if (!response?.success || !Array.isArray(response.segments)) {
                return { segments: [], archivedType: null, availableTypes: response?.availableTypes || [] };
            }

            const segments = response.segments
                .filter(segment => Array.isArray(segment.polyline) && segment.polyline.length > 1)
                .map(segment => ({
                    ...segment,
                    polyline: segment.polyline.map(([lat, lng]) => [Number(lat), Number(lng)])
                }));
            return {
                segments,
                archivedType: response.archivedType || null,
                availableTypes: response.availableTypes || []
            };
        } catch (error) {
            console.error('Error fetching segment polylines:', error);
            return { segments: [], archivedType: null, availableTypes: [] };
        }
    }, [currentEntity?.entityId]);

    // Initial load: fetch segment polylines when advancedMode toggles on or route changes
    useEffect(() => {
        let cancelled = false;

        const loadSegmentPolylines = async () => {
            if (!advancedMode || !selectedRoute?.id) {
                setSegmentPolylines([]);
                setSegmentPolylinesArchivedType(null);
                setAvailableRouteTypes([]);
                setHighlightedSegmentKey(null);
                return;
            }

            setSegmentPolylines([]);
            setSegmentPolylinesArchivedType(null);
            setHighlightedSegmentKey(null);
            // Initial load without explicit type — backend resolves smart default
            const { segments, archivedType, availableTypes } = await fetchSegmentPolylines(selectedRoute.id);
            if (!cancelled) {
                setSegmentPolylines(segments);
                setSegmentPolylinesArchivedType(archivedType);
                setAvailableRouteTypes(availableTypes);
            }
        };

        loadSegmentPolylines();
        return () => {
            cancelled = true;
        };
    }, [advancedMode, selectedRoute?.id, fetchSegmentPolylines]);

    // Handler for archived type selector change
    const handleSegmentTypeChange = useCallback(async (newType) => {
        if (!selectedRoute?.id || newType === segmentPolylinesArchivedType) return;

        setSegmentPolylines([]);
        setHighlightedSegmentKey(null);
        const { segments, archivedType, availableTypes } = await fetchSegmentPolylines(selectedRoute.id, newType);
        setSegmentPolylines(segments);
        if (archivedType) {
            setSegmentPolylinesArchivedType(archivedType);
        }
        if (Array.isArray(availableTypes) && availableTypes.length > 0) {
            setAvailableRouteTypes(availableTypes);
        }
    }, [selectedRoute?.id, segmentPolylinesArchivedType, fetchSegmentPolylines]);

    // Fetch route segments (distance and duration between waypoints)
    const fetchRouteSegments = async (routeId) => {
        if (!routeId || !currentEntity?.entityId) return;

        // Check if we already have segments for this route
        if (routeSegments[routeId]) return;

        try {
            const response = await segmentMatrixService.getSegmentDetails(currentEntity.entityId, routeId);
            if (response.data) {
                setRouteSegments(prev => ({
                    ...prev,
                    [routeId]: response.data
                }));
            }
        } catch (error) {
            console.error('Error fetching route segments:', error);
        }
    };

    // Helper function to get segment info for a waypoint (from previous waypoint)
    const getSegmentForWaypoint = (routeId, toWaypointId) => {
        const segments = routeSegments[routeId];
        if (!segments) return null;

        // Find the sequential segment that ends at this waypoint
        return segments.find(s => s.isSequential && s.toWaypoint.id === toWaypointId);
    };

    // to enable picking mode to pick a stop on the map
    const handlePickStop = (index) => {
        console.log('Setting pick stop with index:', index);
        // In edit mode with filtered view, we need to make sure we're using the correct index
        let actualIndex = index;
        
        if (selectedRoute && !advancedMode) {
            // Get the currently visible stops
            const visibleStops = selectedRoute.waypoints
                .filter(w => w.point_type === 'stop')
                .sort((a, b) => a.sequence_number - b.sequence_number);
            
            if (index < visibleStops.length) {
                // Get the waypoint ID of the visible stop at this position
                const targetWaypointId = visibleStops[index].id;
                
                // Find the index of this waypoint in the full waypoints array
                actualIndex = selectedRoute.waypoints.findIndex(w => w.id === targetWaypointId);
                console.log('Converting UI index', index, 'to actual waypoint index', actualIndex);
            }
        }
        
        setCurrentStopIndex(actualIndex);
        setPickingMode('stop');
    };

    // Handle translation field changes
    const handleTranslationChange = (fieldName, value, isDefault = false) => {
        if (isDefault) {
            // Update the main route content (default language)
            setNewRoute(prev => ({ ...prev, [fieldName]: value }));
        } else {
            // Translation changes are handled automatically by MultiLanguageField
            // and saved via the translation API
            console.log('Translation updated for', fieldName, value);
        }
    };
   
    // handleAddressBlur for manual address entry
    const handleAddressBlur = async (e, index) => {
        const address = e.target.value;
        if (!address) return;
        if (!currentEntity?.entityId) {
            toast.error(t('Failed to geocode address'));
            return;
        }
        
        try {
            const coords = await geocodeAddress(currentEntity.entityId, address);
            
            if (selectedRoute) {
                // For edit mode
                setSelectedRoute(prevRoute => {
                    const updatedWaypoints = prevRoute.waypoints.map((wp, idx) => 
                        idx === index ? {
                            ...wp,
                            address,
                            latitude: Number(coords.lat),
                            longitude: Number(coords.lng)
                        } : wp
                    );
                    
                    return {
                        ...prevRoute,
                        waypoints: [...updatedWaypoints]
                    };
                });
            } else {
                // For add mode
                setStops(prevStops => {
                    const newStops = prevStops.map((stop, idx) => 
                        idx === index ? {
                            ...stop,
                            address,
                            latitude: Number(coords.lat),
                            longitude: Number(coords.lng)
                        } : stop
                    );
                    return [...newStops];
                });
            }
        } catch (error) {
            console.error('Geocoding failed:', error);
            toast.error(t('Failed to geocode address'));
        }
    };

    // Move stop up
    const moveStopUp = (index) => {
        if (index <= 0) return; // Can't move up if first stop
        
        if (selectedRoute) {
            // For edit mode, update selectedRoute.waypoints
            setSelectedRoute(prev => {
                const newWaypoints = [...prev.waypoints];
                [newWaypoints[index - 1], newWaypoints[index]] = [newWaypoints[index], newWaypoints[index - 1]];
                
                // Update sequence numbers and auto-generated names
                return {
                    ...prev,
                    waypoints: newWaypoints.map((waypoint, idx) => ({
                        ...waypoint,
                        // Only change name if it's an auto-generated one
                        name: waypoint.name.startsWith('Stop ') ? `Stop ${idx}` : waypoint.name,
                        sequence_number: idx
                    }))
                };
            });
        } else {
            // For add mode, update stops (existing code)
            setStops(prev => {
                const newStops = [...prev];
                [newStops[index - 1], newStops[index]] = [newStops[index], newStops[index - 1]];
                
                // Update only auto-generated names and sequence numbers
                return newStops.map((stop, idx) => ({
                    ...stop,
                    // Only change name if it's an auto-generated one
                    name: stop.name.startsWith('Stop ') ? `Stop ${idx}` : stop.name,
                    sequence_number: idx
                }));
            });
        }
    };

    // Move stop down
    const moveStopDown = (index) => {
        if (selectedRoute) {
            // For edit mode
            if (index >= selectedRoute.waypoints.length - 1) return; // Can't move down if last stop
            
            setSelectedRoute(prev => {
                const newWaypoints = [...prev.waypoints];
                [newWaypoints[index], newWaypoints[index + 1]] = [newWaypoints[index + 1], newWaypoints[index]];
                
                // Update sequence numbers and auto-generated names
                return {
                    ...prev,
                    waypoints: newWaypoints.map((waypoint, idx) => ({
                        ...waypoint,
                        // Only change name if it's an auto-generated one
                        name: waypoint.name.startsWith('Stop ') ? `Stop ${idx}` : waypoint.name,
                        sequence_number: idx
                    }))
                };
            });
        } else {
            // For add mode (existing code)
            if (index >= stops.length - 1) return; // Can't move down if last stop
            
            setStops(prev => {
                const newStops = [...prev];
                [newStops[index], newStops[index + 1]] = [newStops[index + 1], newStops[index]];
                
                // Update only auto-generated names and sequence numbers
                return newStops.map((stop, idx) => ({
                    ...stop,
                    // Only change name if it's an auto-generated one
                    name: stop.name.startsWith('Stop ') ? `Stop ${idx}` : stop.name,
                    sequence_number: idx
                }));
            });
        }
    };
    
    // Fetching routes
    const fetchRoutes = useCallback(async (entityId) => {
        try {
            const response = await axios.get(
                `/api/routes/${entityId}`,
                { params: { includeInactive: true, include: 'stops' } }
            );

            const defaultLanguage = entitySettings?.entity_default_language || currentEntity?.entity_default_language || 'en';
            const shouldTranslateContent = entitySettings?.multi_language_enabled === true && i18n.language !== defaultLanguage;

            const fetchedRoutes = shouldTranslateContent
                ? await translationService.translateRoutesWithWaypoints(response.data, {
                    entityId,
                    userLanguage: i18n.language,
                    defaultLanguage,
                    mode: 'display'
                })
                : response.data;

            setRoutes(fetchedRoutes);
            setFilteredRoutes(fetchedRoutes);
        } catch (error) {
            console.error('Error fetching routes:', error);
            toast.error(t('errorFetchingRoutes'));
        }
    }, [
        currentEntity?.entity_default_language,
        entitySettings?.entity_default_language,
        entitySettings?.multi_language_enabled,
        i18n.language,
        t
    ]);

    useEffect(() => {
        if (currentEntity?.entityId) {
            fetchRoutes(currentEntity.entityId);
        }
    }, [currentEntity?.entityId, fetchRoutes]);

    // Filter routes based on search term
    useEffect(() => {

        // First filter by active status
        let filtered = showInactive 
            ? routes 
            : routes.filter(route => route.status === 'active');

         // Then apply search term if it exists
        if (searchTerm.trim() !== '') {
            const searchTermLower = searchTerm.toLowerCase();
            filtered = filtered.filter(route => {
                // Search in route name
                const routeName = getRouteDisplayName(route).toLowerCase();
                const originalRouteName = (route.name || '').toLowerCase();
                if (routeName.includes(searchTermLower) || originalRouteName.includes(searchTermLower)) {
                    return true;
                }
                
                // Search in waypoints (including stops, waypoints, and turn points)
                if (route.waypoints && route.waypoints.length > 0) {
                    return route.waypoints.some(waypoint => 
                        getWaypointDisplayName(waypoint).toLowerCase().includes(searchTermLower) ||
                        getWaypointDisplayAddress(waypoint).toLowerCase().includes(searchTermLower) ||
                        (waypoint.name || '').toLowerCase().includes(searchTermLower) ||
                        (waypoint.address || '').toLowerCase().includes(searchTermLower)
                    );
                }
                
                return false;
            });
        }
        
        setFilteredRoutes(filtered);
    }, [getRouteDisplayName, getWaypointDisplayAddress, getWaypointDisplayName, searchTerm, routes, showInactive]);

    const handleSubmit = async (e) => {
        e.preventDefault();
    
        if (!newRoute.name?.trim()) {
            toast.error(t('Route name is required'));
            return;
        }
    
        if (stops.length < 2) {
            toast.error(t('At least start and end locations are required'));
            return;
        }
        
        setIsLoading(true);
        try {
            // Process waypoints to ensure proper boolean values
            const processWaypoint = (waypoint, index) => {
                return {
                    ...waypoint,
                    sequence_number: index,
                    // Convert to actual booleans for database
                    can_pickup: waypoint.point_type === 'stop' ? Boolean(waypoint.can_pickup) : false,
                    can_dropoff: waypoint.point_type === 'stop' ? Boolean(waypoint.can_dropoff) : false,
                    // Ensure proper number values
                    stay_duration_minutes: waypoint.point_type === 'stop'
                        ? Number(waypoint.stay_duration_minutes || 0)
                        : 0,
                    // Use the actual is_required value from the waypoint (default true for stops and waypoints)
                    is_required: (waypoint.point_type === 'stop' || waypoint.point_type === 'waypoint')
                        ? (waypoint.is_required !== false)
                        : true,
                    // Use the actual can_reorder value from the waypoint (default false for stops and waypoints)
                    can_reorder: (waypoint.point_type === 'stop' || waypoint.point_type === 'waypoint')
                        ? (waypoint.can_reorder === true)
                        : false
                };
            };
            const routeData = {
                name: newRoute.name,
                status: newRoute.status || 'active',
                description: newRoute.description || '',
                waypoints: selectedRoute ? 
                    // When editing a route, ensure unique sequence numbers
                    selectedRoute.waypoints.map((waypoint, index) => ({
                        ...processWaypoint(waypoint, index),
                        sequence_number: index // Override with new sequence numbers
                    })) :
                    // New route - use whatever is in the form
                    stops.map(processWaypoint)                                      
                        
            };
            console.log('routeData in handleSubmit of RoutesManagement.js: ', routeData);
            if (selectedRoute) {
                // Update existing route
                await axios.put(
                    `/api/routes/${currentEntity.entityId}/${selectedRoute.id}`, 
                    routeData
                );
                toast.success(t('routeUpdatedSuccessfully'));
            } else {
                // Create new route 
                await axios.post(
                    `/api/routes/${currentEntity.entityId}`, 
                    routeData
                );
                toast.success(t('routeCreatedSuccessfully'));
            }
    
            // Reset form and refresh routes
            setNewRoute({
                name: '',
                status: 'active',
                description: '',
                route_version: 'default'
            });
            setStops([]);
            setSelectedRoute(null);
            setShowAddForm(false);
            setShowEditForm(null);
            fetchRoutes(currentEntity.entityId);
        
        } catch (error) {
            console.error('Error saving route:', error);
            toast.error(t('errorSavingRoute'));
        } finally {
            setIsLoading(false);
        }
    };  

    // Functions for handling stops
    const handleStopChange = (e, index, field, value) => {
        // console.log('Checkbox change:', { field, value, index, checked: e.target?.checked });

        if (selectedRoute) {
            // For edit mode
            setSelectedRoute(prev => {
                const updatedWaypoints = [...prev.waypoints];
                
                if (field === 'point_type') {
                    const newType = e.target.value;
                    // When changing point type, preserve existing pickup/dropoff values if they exist
                    updatedWaypoints[index] = {
                        ...updatedWaypoints[index],
                        [field]: newType,
                        // Only initialize these if they don't already exist
                        can_pickup: newType === 'stop' ?(updatedWaypoints[index].can_pickup ?? false) : false,
                        can_dropoff: newType === 'stop' ? (updatedWaypoints[index].can_dropoff ?? false) : false,
                        stay_duration_minutes: newType === 'stop' 
                            ? (updatedWaypoints[index].stay_duration_minutes || 1) 
                            : 0
                    };                  
                
                } else if (field === 'can_pickup' || field === 'can_dropoff') {
                    // can_pickup/can_dropoff only apply to stops
                    console.log('Setting checkbox:', field, 'to', value);
                    updatedWaypoints[index] = {
                        ...updatedWaypoints[index],
                        [field]: updatedWaypoints[index].point_type === 'stop' ? Boolean(value) : false
                    };
                } else if (field === 'is_required' || field === 'can_reorder') {
                    // is_required/can_reorder can apply to any point type (stop, waypoint, etc.)
                    console.log('Setting checkbox:', field, 'to', value);
                    updatedWaypoints[index] = {
                        ...updatedWaypoints[index],
                        [field]: Boolean(value)
                    };
                } else if (field === 'latitude' || field === 'longitude') {
                    updatedWaypoints[index] = { 
                        ...updatedWaypoints[index], 
                        [field]: Number(e.target.value) // Ensure number for coordinates
                    };                
                } else {
                    updatedWaypoints[index] = { 
                        ...updatedWaypoints[index], 
                        [field]: value ?? e.target.value 
                    };
                }
                console.log('Updated waypoint can_pickup:', updatedWaypoints[index].can_pickup);
                console.log('Updated waypoint can_dropoff:', updatedWaypoints[index].can_dropoff);
                console.log(`Changed point_type to ${updatedWaypoints[index].point_type}:`, updatedWaypoints[index]);
                return {
                    ...prev,
                    waypoints: updatedWaypoints
                };
            });
        } else { 
            // For add mode
            setStops(prevStops => {
                const newStops = [...prevStops];
                if (field === 'point_type') {
                    newStops[index] = {
                        ...newStops[index],
                        [field]: e.target.value,
                        can_pickup: e.target.value === 'stop' ? false : false,
                        can_dropoff: e.target.value === 'stop' ? false : false,
                        stay_duration_minutes: e.target.value === 'stop' ? 1 : 0
                    };
                } else if (field === 'can_pickup' || field === 'can_dropoff') {
                    // can_pickup/can_dropoff only apply to stops
                    newStops[index] = {
                        ...newStops[index],
                        [field]: newStops[index].point_type === 'stop' ? Boolean(value) : false
                    };
                } else if (field === 'is_required' || field === 'can_reorder') {
                    // is_required/can_reorder can apply to any point type
                    newStops[index] = {
                        ...newStops[index],
                        [field]: Boolean(value)
                    };
                } else if (field === 'latitude' || field === 'longitude') {
                    newStops[index] = { 
                        ...newStops[index], 
                        [field]: Number(e.target.value) // Ensure number for coordinates
                    };                
                } else {
                    newStops[index] = {
                        ...newStops[index],
                        [field]: value ?? e.target.value
                    };
                }
                // console.log('New stop:', newStops[index]);
                return newStops;
            });
        }
    };

    const handleRemoveStop = (index) => {
        if (selectedRoute) {
            setSelectedRoute(prev => {
                const waypoints = [...prev.waypoints];
                waypoints.splice(index, 1);
                
                // Just re-sequence all waypoints
                return {
                    ...prev,
                    waypoints: waypoints.map((w, i) => ({
                        ...w,
                        sequence_number: i
                    }))
                };
            });
        } else {
            // For new routes
            setStops(prevStops => {
                const newStops = prevStops.filter((_, i) => i !== index);
                return newStops.map((stop, i) => ({
                    ...stop,
                    sequence_number: i
                }));
            });
        }
    };

    const handleAddStop = () => {
        // Return early if editing is restricted
        if (restrictEditingInNormalMode) {
            return;
        }
    
        if (selectedRoute) {
            // When editing an existing route
            setSelectedRoute(prev => {
                const newStopIndex = prev.waypoints.length - 1;
                const endStop = prev.waypoints[prev.waypoints.length - 1];
                
                return {
                    ...prev,
                    waypoints: [
                        ...prev.waypoints.slice(0, -1),
                        // Add new intermediate stop
                        {
                            name: `Stop ${newStopIndex}`,
                            address: '',
                            latitude: null,
                            longitude: null,
                            point_type: 'stop',
                            can_pickup: true,
                            can_dropoff: true,
                            stay_duration_minutes: 1,
                            is_required: true,
                            can_reorder: false,
                            sequence_number: newStopIndex,
                            boarding_location_info: '',
                            boarding_location_url: '',
                            boarding_location_image: null
                        },
                        // Add the original end stop back
                        endStop
                    ]
                };
            });

            // Set timeout to scroll to the new stop after render
            setTimeout(() => {
                const newStopElement = document.getElementById(`waypoint-${selectedRoute.waypoints.length - 1}`);
                if (newStopElement) {
                    newStopElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 100);
        } else {
            // For new routes
            const newStopIndex = stops.length - 1;
            const endStop = stops[stops.length - 1];
    
            setStops(prev => [
                ...prev.slice(0, -1),
                {
                    name: `Stop ${newStopIndex}`,
                    address: '',
                    latitude: null,
                    longitude: null,
                    point_type: 'stop',
                    can_pickup: true,
                    can_dropoff: true,
                    stay_duration_minutes: 1,
                    is_required: true,
                    can_reorder: false,
                    boarding_location_info: '',
                    boarding_location_url: '',
                    boarding_location_image: null
                },
                endStop
            ]);
            // Set timeout to scroll to the new stop after render            
            setTimeout(() => {
                const newStopElement = document.getElementById(`stop-${stops.length - 1}`);
                if (newStopElement) {
                    newStopElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 100);
        }       
    };

    // When showing the add form, initialize with at least two stops
    const handleShowAddForm = () => {
        setSelectedRoute(null);
        setShowAddForm(true);
        setShowEditForm(null);
        setNewRoute({
            name: '',
            status: 'active',
            description: '',
            route_version: 'default'
        });
        // Initialize with start and end stops
        setStops([
            {
                name: t('startLocation'),
                address: '',
                latitude: null,
                longitude: null,
                point_type: 'stop',
                can_pickup: true,
                can_dropoff: false,
                stay_duration_minutes: 1,
                is_required: true,
                can_reorder: false,
                boarding_location_info: '',
                boarding_location_url: '',
                boarding_location_image: null
            },
            {
                name: t('endLocation'),
                address: '',
                latitude: null,
                longitude: null,
                point_type: 'stop',
                can_pickup: false,
                can_dropoff: true,
                stay_duration_minutes: 1,
                is_required: true,
                can_reorder: false,
                boarding_location_info: '',
                boarding_location_url: '',
                boarding_location_image: null
            }
        ]);
    };

    const handleCancelForm = () => {
        setShowAddForm(false);
        setSelectedRoute(null);
        setEditingRouteId(null);
    };

    // activate / deactivate a route

    // Update the status change handler
    const handleStatusChange = async (route) => {
        setRouteToUpdate(route);
        
        // Ensure dialog opens only after routeToUpdate is set
        setTimeout(() => setShowStatusChangeDialog(true), 0);
    };

    const handleStatusChangeConfirm = useCallback(async () => {
        if (!routeToUpdate) return;

        setIsProcessing(true);
        try {
            const newStatus = routeToUpdate.status === 'active' ? 'inactive' : 'active';
            await axios.put(`/api/routes/${currentEntity.entityId}/${routeToUpdate.id}/status`, {
                status: newStatus
            });
            
            // Update local state
            setRoutes(prevRoutes => 
                prevRoutes.map(r => 
                    r.id === routeToUpdate.id 
                        ? { ...r, status: newStatus }
                        : r
                )
            );
            
            toast.success(t(`Route ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`));

            setShowStatusChangeDialog(false);
            setRouteToUpdate(null);
        } catch (error) {
            console.error('Error updating route status:', error);
            toast.error(t('Failed to update route status'));
        } finally {
            setIsProcessing(false);
        }
    }, [routeToUpdate, isProcessing, t]);  

    // Handler for closing the dialog
    const handleDialogClose = () => {
        setShowStatusChangeDialog(false);
        setRouteToUpdate(null);
    };

    const handleDeleteClick = (route, e) => {
        e.stopPropagation();
        setRouteToDelete(route);
        setShowDeleteConfirmation(true);
    };

    const handleDeleteConfirm = useCallback(async () => {
        if (routeToDelete && !isDeleting) {
            setIsDeleting(true);
            try {
                await axios.delete(`/api/routes/${currentEntity.entityId}/${routeToDelete.id}`);
                setRoutes(prevRoutes => prevRoutes.filter(route => route.id !== routeToDelete.id));
                toast.success(routeToDelete.name + ": " + t('RouteDeletedSuccessfully'));
                setSelectedRoute(null);
            } catch (error) {
                console.error('Error deleting route:', error);
                toast.error(t('errorDeletingRoute'));
            } finally {
                setShowDeleteConfirmation(false);
                setRouteToDelete(null);
                setIsDeleting(false);
            }
        }
    }, [routeToDelete, isDeleting, t]);    



    // handle edit route called by the edit button
    const handleEditRoute = (route) => {
        console.log('Route being edited:', route);
        console.log('All waypoints:', route.waypoints);

        const orderedWaypoints = route.waypoints?.sort((a, b) => a.sequence_number - b.sequence_number) || [];
        
        // format waypoints
        const formattedWaypoints = orderedWaypoints.map((waypoint, index) => ({
                ...waypoint,
                point_type: waypoint.point_type || 'stop',
                can_pickup: Boolean(waypoint.can_pickup),
                can_dropoff: Boolean(waypoint.can_dropoff),
                stay_duration_minutes: waypoint.stay_duration_minutes || 0,
                sequence_number: index,
                is_required: waypoint.is_required ?? true
            }));

        // Set selectedRoute with properly formatted waypoints
        setSelectedRoute({
            ...route,
            waypoints: formattedWaypoints
        });
        
        setNewRoute({
            name: route.name,            
            status: route.status || 'active',
            description: route.description || ''
        });
    
        setStops(formattedWaypoints);
    
        if (formattedWaypoints.length > 0) {
            setMapCenter([
                parseFloat(formattedWaypoints[0].latitude), 
                parseFloat(formattedWaypoints[0].longitude)
            ]);
        }
    
        setShowDefaultMarker(false); 
        setShowEditForm(route.id);
        setShowAddForm(false);
        setEditingRouteId(route.id);
    };
    
    const handleRouteClick = (route) => {
        // set selected route   
        setSelectedRoute(prevSelectedRoute => {
            const newSelectedRoute = prevSelectedRoute && prevSelectedRoute.id === route.id ? null : route;
            
            // Clear route reference path if unselecting a route
            if (!newSelectedRoute) {
              setRouteReferenceLocations(null);
              setSegmentPolylines([]);
              setSegmentPolylinesArchivedType(null);
              setAvailableRouteTypes([]);
              setHighlightedSegmentKey(null);
              return null;
            } else {
              // Fetch route reference path for the selected route
              fetchRouteReferenceLocations(route.id);
              // Fetch segment data (distance/duration) for the route
              fetchRouteSegments(route.id);

              // Ensure waypoint boolean properties are properly converted
              const processedRoute = {
                ...newSelectedRoute,
                waypoints: newSelectedRoute.waypoints?.map(waypoint => ({
                  ...waypoint,
                  can_pickup: Boolean(waypoint.can_pickup),
                  can_dropoff: Boolean(waypoint.can_dropoff),
                  is_required: waypoint.is_required !== 0 && waypoint.is_required !== false
                })) || []
              };
              
              return processedRoute;
            }
        });

        setShowAddForm(false);
        setShowEditForm(null);
        setShowDeleteConfirmation(false);
        setShowDefaultMarker(false); // Hide the default marker when a route is selected
    };

    const formatDistanceFromMeters = useCallback((distanceM) => (
        formatDistanceMeters(distanceM, distanceUnit, t, {
            compact: false,
            zeroLabel: getZeroDistanceLabel(distanceUnit, t)
        })
    ), [distanceUnit, t]);

    const formatDistanceFromKilometers = useCallback((distanceKm) => (
        formatDistanceKilometers(distanceKm, distanceUnit, t, {
            compact: false,
            zeroLabel: getZeroDistanceLabel(distanceUnit, t)
        })
    ), [distanceUnit, t]);

    const formatOptionalDistanceFromMeters = useCallback((distanceM) => {
        const numeric = Number(distanceM);
        if (!Number.isFinite(numeric)) return 'n/a';
        return formatDistanceFromMeters(numeric);
    }, [formatDistanceFromMeters]);

    const getSegmentKey = useCallback((segment) => (
        `${segment.fromWaypointId}-${segment.toWaypointId}-${segment.variant || 'standard'}`
    ), []);

    const getDecimatedBreadcrumbPoints = useCallback((polyline) => {
        if (!Array.isArray(polyline) || polyline.length === 0) {
            return [];
        }
        if (polyline.length <= BREADCRUMB_MAX_POINTS) {
            return polyline;
        }

        const step = Math.ceil(polyline.length / BREADCRUMB_MAX_POINTS);
        return polyline.filter((_, index) => (
            index % step === 0 || index === polyline.length - 1
        ));
    }, []);

    const waypointById = useMemo(() => {
        const map = new Map();
        const waypoints = selectedRoute?.waypoints || [];
        for (const waypoint of waypoints) {
            map.set(waypoint.id, waypoint);
        }
        return map;
    }, [selectedRoute?.waypoints]);

    const highlightedSegmentDebug = useMemo(() => {
        if (!highlightedSegmentKey) return null;
        const segment = segmentPolylines.find(
            currentSegment => getSegmentKey(currentSegment) === highlightedSegmentKey
        );
        if (!segment) return null;

        const fromWaypoint = waypointById.get(segment.fromWaypointId);
        const toWaypoint = waypointById.get(segment.toWaypointId);
        const fromCoord = parseWaypointCoord(fromWaypoint);
        const toCoord = parseWaypointCoord(toWaypoint);
        const polyline = Array.isArray(segment.polyline) ? segment.polyline : [];
        const firstPoint = parsePolylinePoint(polyline[0]);
        const lastPoint = parsePolylinePoint(polyline[polyline.length - 1]);

        const startSnapOffsetM = (fromCoord && firstPoint)
            ? haversineDistanceMeters(fromCoord, firstPoint)
            : null;
        const endSnapOffsetM = (toCoord && lastPoint)
            ? haversineDistanceMeters(toCoord, lastPoint)
            : null;
        const straightLineDistanceM = (fromCoord && toCoord)
            ? haversineDistanceMeters(fromCoord, toCoord)
            : null;
        const routeToStraightRatio = (straightLineDistanceM && straightLineDistanceM > 0)
            ? (Number(segment.totalDistanceM) / straightLineDistanceM)
            : null;

        const firstYear = segment.firstTimestamp ? new Date(segment.firstTimestamp).getUTCFullYear() : null;
        const lastYear = segment.lastTimestamp ? new Date(segment.lastTimestamp).getUTCFullYear() : null;
        const isSyntheticORS = firstYear === 2000 && lastYear === 2000;

        const fromSeq = Number(segment.fromSequence);
        const toSeq = Number(segment.toSequence);
        const sequenceGap = (Number.isFinite(fromSeq) && Number.isFinite(toSeq))
            ? Math.abs(toSeq - fromSeq)
            : null;

        return {
            segment,
            fromCoord,
            toCoord,
            firstPoint,
            lastPoint,
            startSnapOffsetM,
            endSnapOffsetM,
            straightLineDistanceM,
            routeToStraightRatio,
            sequenceGap,
            sourceHint: isSyntheticORS ? 'ORS synthetic reference (timestamp baseline year 2000)' : 'Recorded trip/GPS reference'
        };
    }, [highlightedSegmentKey, segmentPolylines, getSegmentKey, waypointById]);

    const maxHeight = routes.length === 0 ? 'max-h-[20%]' : 'max-h-[40%]';

    const handleLocationPick = (lat, lng, address) => {
        if (currentStopIndex === null) {
            console.error('No stop selected for location picking');
            return;
        }
    
        console.log('Location picked:', { lat, lng, address, currentStopIndex });
    
        if (showEditForm) {
            // For edit mode - update the selectedRoute waypoints
            setSelectedRoute(prevRoute => {
                const updatedWaypoints = [...prevRoute.waypoints];
                updatedWaypoints[currentStopIndex] = {
                    ...updatedWaypoints[currentStopIndex],
                    address: address,
                    latitude: parseFloat(lat.toFixed(8)),
                    longitude: parseFloat(lng.toFixed(8))
                };
                
                console.log('Updated waypoint:', updatedWaypoints[currentStopIndex]);
                
                return {
                    ...prevRoute,
                    waypoints: updatedWaypoints
                };
            });
        } else {
            // For add mode - update the stops array
            setStops(prevStops => {
                const newStops = [...prevStops];
                newStops[currentStopIndex] = {
                    ...newStops[currentStopIndex],
                    address: address,
                    latitude: parseFloat(lat.toFixed(8)),
                    longitude: parseFloat(lng.toFixed(8))
                };
                
                console.log('Updated stop:', newStops[currentStopIndex]);
                
                return newStops;
            });
        }
    
        // Reset picking mode
        setPickingMode(null);
        setCurrentStopIndex(null);
    };
    
    const handleMapChange = (newCenter, newZoom) => {
        setMapCenter(newCenter);
        setMapZoom(newZoom);
    };

    // Boarding image handlers
    const handleBoardingImageSelect = (index, event) => {
        const file = event.target.files[0];
        if (file) {
            if (file.size > 2 * 1024 * 1024) {
                toast.error(t('boardingImageSizeError'));
                return;
            }

            setBoardingImages(prev => ({ ...prev, [index]: file }));

            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setBoardingImagePreviews(prev => ({ ...prev, [index]: reader.result }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleBoardingImageUpload = async (index, waypointId) => {
        const file = boardingImages[index];
        if (!file || !waypointId || !currentEntity?.entityId) return;

        setUploadingImages(prev => ({ ...prev, [index]: true }));

        const formData = new FormData();
        formData.append('boardingImage', file);

        try {
            const response = await axios.post(
                `/api/waypoints/${currentEntity.entityId}/${waypointId}/boarding-image`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            if (response.data.success) {
                toast.success(t('boardingImageUploadedSuccessfully'));
                // Update waypoint data with new image URL
                handleStopChange(
                    { target: { value: response.data.imageUrl } },
                    index,
                    'boarding_location_image'
                );
                // Clear the file from state
                setBoardingImages(prev => {
                    const updated = { ...prev };
                    delete updated[index];
                    return updated;
                });
            }
        } catch (error) {
            toast.error(t('errorUploadingBoardingImage'));
            console.error('Error uploading boarding image:', error);
        } finally {
            setUploadingImages(prev => ({ ...prev, [index]: false }));
        }
    };

    const handleBoardingImageDelete = async (index, waypointId) => {
        if (!waypointId || !currentEntity?.entityId) return;

        try {
            await axios.delete(`/api/waypoints/${currentEntity.entityId}/${waypointId}/boarding-image`);
            toast.success(t('boardingImageDeleted'));
            handleStopChange(
                { target: { value: null } },
                index,
                'boarding_location_image'
            );
            setBoardingImagePreviews(prev => {
                const updated = { ...prev };
                delete updated[index];
                return updated;
            });
        } catch (error) {
            toast.error(t('errorDeletingBoardingImage'));
            console.error('Error deleting boarding image:', error);
        }
    };

    const clearBoardingImageSelection = (index) => {
        setBoardingImages(prev => {
            const updated = { ...prev };
            delete updated[index];
            return updated;
        });
        setBoardingImagePreviews(prev => {
            const updated = { ...prev };
            delete updated[index];
            return updated;
        });
    };

    return (
        <div className={cn("flex flex-col md:flex-row h-screen overflow-hidden z-[-1]", background.primary)} style={{ height: 'calc(100vh - 54px)' }}> {/* Adjust height dynamically */}
            {/* Left side: Route List and Form */}
            <div className={cn(`md:w-2/5 w-full flex-grow overflow-y-auto flex-shrink-0 ${maxHeight} md:max-h-full`, background.secondary)}>
                <div className={cn("p-4", background.primary)}>
                    <div className="flex items-center justify-between mb-4">
                        <h2 className={cn("text-2xl font-bold", text.primary)}>
                            {t('RouteManagement')} ({routes.length})
                        </h2>
                        <div className="flex space-x-4">
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="sr-only peer"
                                    checked={advancedMode}
                                    onChange={(e) => setAdvancedMode(e.target.checked)}
                                />
                                <div className={cn(
                                    "w-9 h-5 rounded-full peer transition-colors",
                                    "peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-600",
                                    "peer-checked:after:translate-x-full peer-checked:bg-blue-600",
                                    "after:content-[''] after:absolute after:top-0.5 after:left-[2px]",
                                    "after:border after:rounded-full after:h-4 after:w-4 after:transition-all",
                                    "bg-gray-200 dark:bg-gray-700",
                                    "after:bg-white after:border-gray-300 dark:after:border-gray-600"
                                )}>
                                </div>
                                <span className={cn("ml-3 text-sm font-medium", text.secondary)}>{t('advancedMode')}</span>
                            </label>
                            {hasInactiveRoutes && (
                                <div className="flex items-center ">
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            className="sr-only peer" 
                                            checked={showInactive}
                                            onChange={(e) => setShowInactive(e.target.checked)}
                                        />
                                        <div className={cn(
                                            "w-9 h-5 rounded-full peer transition-colors",
                                            "peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-600",
                                            "peer-checked:after:translate-x-full peer-checked:bg-blue-600",
                                            "after:content-[''] after:absolute after:top-0.5 after:left-[2px]",
                                            "after:border after:rounded-full after:h-4 after:w-4 after:transition-all",
                                            "bg-gray-200 dark:bg-gray-700",
                                            "after:bg-white after:border-gray-300 dark:after:border-gray-600"
                                        )}>
                                        </div>
                                        <span className={cn("ml-3 text-sm font-medium", text.secondary)}>
                                            {t('showInactive')}
                                        </span>
                                    </label>
                                </div>
                            )}
                        </div>
                    </div>
                
                    <div className="flex items-center gap-8 mb-2">
                        <ThemedButton
                            onClick={handleShowAddForm}
                            className="whitespace-nowrap px-6"
                        >
                            {t('addRoute')}
                        </ThemedButton>

                        {/* Show filter input when routes exceed the max allowed */}
                        {routes.length >= globalThreshold && (
                            <ThemedInput
                                type="text"
                                className="w-48"
                                placeholder={t('searchRoutes')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        )}
                    </div>

                    {/* Add Form */}
                    {showAddForm && (
                        <ThemedCard className="flex-grow overflow-y-auto max-h-[calc(100vh-250px)]">
                            <ThemedCard.Body>
                            <form onSubmit={handleSubmit}>
                                <div className="mb-4">
                                    <MultiLanguageField
                                        entityId={currentEntity?.entityId}
                                        entityDefaultLanguage={entitySettings?.entity_default_language || 'en'}
                                        multiLanguageEnabled={entitySettings?.multi_language_enabled || false}
                                        contentType="route"
                                        contentId={null} // null for new routes
                                        fieldName="name"
                                        defaultValue={newRoute.name}
                                        onChange={handleTranslationChange}
                                        label={t('routeName')}
                                        required={true}
                                        multiline={false}
                                        disabled={false}
                                    />
                                </div>
                                <div className="mb-4">
                                    <MultiLanguageField
                                        entityId={currentEntity?.entityId}
                                        entityDefaultLanguage={entitySettings?.entity_default_language || 'en'}
                                        multiLanguageEnabled={entitySettings?.multi_language_enabled || false}
                                        contentType="route"
                                        contentId={null} // null for new routes
                                        fieldName="description"
                                        defaultValue={newRoute.description || ''}
                                        onChange={handleTranslationChange}
                                        label={t('description')}
                                        required={false}
                                        multiline={true}
                                        disabled={false}
                                    />
                                </div>

                                {/* Waypoint/Stop Cards */}
                                {stops.map((waypoint, index) => (
                                    <WaypointFormCard
                                        key={index}
                                        waypoint={waypoint}
                                        index={index}
                                        isFirst={index === 0}
                                        isLast={index === stops.length - 1}
                                        totalCount={stops.length}
                                        mode="add"
                                        advancedMode={advancedMode}
                                        restrictedEditing={false}
                                        onStopChange={handleStopChange}
                                        onMoveUp={moveStopUp}
                                        onMoveDown={moveStopDown}
                                        onRemove={handleRemoveStop}
                                        onPickLocation={handlePickStop}
                                        onAddressBlur={handleAddressBlur}
                                        pickingMode={pickingMode}
                                        currentStopIndex={currentStopIndex}
                                        onCancelPicking={() => setPickingMode(null)}
                                        boardingImagePreview={boardingImagePreviews[index]}
                                        boardingImage={boardingImages[index]}
                                        onImageSelect={handleBoardingImageSelect}
                                        onImageUpload={handleBoardingImageUpload}
                                        onImageDelete={handleBoardingImageDelete}
                                        onClearImageSelection={clearBoardingImageSelection}
                                        uploadingImage={uploadingImages[index]}
                                        entityId={currentEntity?.entityId}
                                        entitySettings={entitySettings}
                                    />
                                ))}

                                <ThemedButton 
                                    type="button" 
                                    variant="secondary"
                                    className="mb-2" 
                                    onClick={handleAddStop}
                                >
                                    {t('addStop')}
                                </ThemedButton>
                                <ThemedButton 
                                    type="submit" 
                                    className="w-full mb-2"
                                >
                                    {selectedRoute ? t('updateShuttleRoute') : t('createRoute')}
                                </ThemedButton>
                                <ThemedButton 
                                    type="button" 
                                    variant="secondary"
                                    onClick={handleCancelForm}
                                >
                                    {t('cancel')}
                                </ThemedButton>
                            </form>
                            </ThemedCard.Body>
                        </ThemedCard>
                    )}

                </div>
                {/* List of Routes */}
                <div className="flex-grow overflow-y-auto">
                    {filteredRoutes.map((route) => {
                        const orderedWaypoints = route.waypoints?.sort((a, b) => a.sequence_number - b.sequence_number) || [];

                        return (
                            <ThemedCard 
                                key={route.id}
                                className={cn(
                                    "m-2 cursor-pointer transition-all duration-200",
                                    selectedRoute && selectedRoute.id === route.id 
                                        ? "ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30" 
                                        : cn(components.card.hover, "hover:shadow-md")
                                )}
                                
                                onClick={() => handleRouteClick(route)}
                            >
                                <ThemedCard.Body>
                                <div className="flex justify-between items-center">
                                    <h3 className={cn("font-bold text-lg", text.primary)}>{getRouteDisplayName(route)}</h3>
                                    <span className={cn(
                                        "px-2 py-1 rounded text-sm",
                                        route.status === 'inactive' 
                                            ? "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300" 
                                            : "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300"
                                    )}>
                                        {t(route.status)}
                                    </span>
                                </div>

                                {orderedWaypoints.length > 0 && (
                                    <div className="mt-2">
                                        <ul className="list-none">
                                            {(advancedMode ? orderedWaypoints : orderedWaypoints.filter(wp => wp.point_type === 'stop'))
                                                .map((waypoint, index, filteredArray) => (
                                                    <li key={waypoint.id} className={cn(
                                                        "mb-1",
                                                        waypoint.point_type === 'stop' ? 'ml-4' : 'ml-8',
                                                        index === 0 ? 'text-blue-600 dark:text-blue-400 font-medium' : 
                                                        index === filteredArray.length - 1 ? 'text-red-600 dark:text-red-400 font-medium' : 
                                                        text.secondary
                                                    )}
                                                    >
                                                        <span>
                                                            {waypoint.point_type === 'stop' && (
                                                                index === 0 ? `🚏 ${t('start')}: ` : 
                                                                index === filteredArray.length - 1 ? `🏁 ${t('end')}: ` : 
                                                                `📌 ${t('stop')}: `
                                                            )}
                                                            {advancedMode && waypoint.point_type === 'waypoint' && `📍 ${t('waypoint')}: `}
                                                            {getWaypointDisplayName(waypoint)} - {getWaypointDisplayAddress(waypoint)}
                                                        </span>

                                                        {/* Segment distance/duration from previous waypoint */}
                                                        {index > 0 && (() => {
                                                            const segment = getSegmentForWaypoint(route.id, waypoint.id);
                                                            if (segment) {
                                                                return (
                                                                    <div className={cn("text-xs ml-6 mt-1", text.muted)}>
                                                                        📏 Distance: {formatDistanceFromKilometers(segment.distanceKm)} • ⏱️ Duration: {segment.durationMinutes} {t('min')}
                                                                    </div>
                                                                );
                                                            }
                                                            return null;
                                                        })()}

                                                        <div className={cn("text-sm ml-6", text.muted)}>
                                                            {waypoint.point_type === 'stop' && (
                                                                <span>
                                                                    {waypoint.can_pickup ? `↗️ ${t('pickup')} ` : ''}
                                                                    {waypoint.can_dropoff ? `↘️ ${t('dropoff')} ` : ''}
                                                                    {waypoint.stay_duration_minutes > 0 &&
                                                                        `⏱️ ${t('Stay')}: ${waypoint.stay_duration_minutes} ${t('min')} `}
                                                                </span>
                                                            )}
                                                            {(waypoint.point_type === 'stop' || waypoint.point_type === 'waypoint') && (
                                                                <span>
                                                                    {waypoint.is_required !== false
                                                                        ? `🔒 ${t('alwaysVisit')} `
                                                                        : `⏭️ ${t('canSkip')} `}
                                                                    {waypoint.can_reorder === true
                                                                        ? `🔀 ${t('canReorder')} `
                                                                        : `📌 ${t('fixedPosition')} `}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </li>
                                                ))}
                                        </ul>
                                    </div>
                                )}

                                {selectedRoute && selectedRoute.id === route.id && (
                                    <div className="mt-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                                        <div className="flex flex-wrap gap-2">
                                            <ThemedButton
                                                variant="secondary"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleEditRoute(route);
                                                }}
                                                icon={Edit}
                                                size="sm"
                                            >
                                                {t('edit')}
                                            </ThemedButton>
                                            <ThemedButton
                                                variant="secondary"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSegmentMatrixRoute(route);
                                                    setShowSegmentMatrixModal(true);
                                                }}
                                                icon={Calculator}
                                                size="sm"
                                                title={t('manageTravelTimes')}
                                            >
                                                {t('travelTimes')}
                                            </ThemedButton>
                                            <ThemedButton
                                                variant="danger"
                                                onClick={(e) => handleDeleteClick(route, e)}
                                                icon={Delete}
                                                size="sm"
                                            >
                                                {t('delete')}
                                            </ThemedButton>
                                            <ThemedButton 
                                                variant={route.status === 'active' ? 'secondary' : 'primary'}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleStatusChange(route);
                                                }}
                                                icon={AlertCircle}
                                                size="sm"
                                            >
                                                {route.status === 'active' ? t('Deactivate') : t('Activate')}
                                            </ThemedButton>

                                            {/* Status Change Confirmation Dialog */}
                                            <StatusChangeConfirmDialog
                                                isOpen={showStatusChangeDialog && routeToUpdate?.id === route.id}
                                                onClose={handleDialogClose}
                                                onConfirm={handleStatusChangeConfirm}
                                                currentStatus={routeToUpdate?.status}
                                                title={
                                                    routeToUpdate?.status === 'active' 
                                                        ? t('Confirm Deactivation') 
                                                        : t('Confirm Activation')
                                                }
                                                isProcessing={isProcessing}
                                            />
                                        </div>

                                        {/* Delete Confirmation Dialog */}
                                        <DeleteConfirmDialog
                                            isOpen={showDeleteConfirmation && routeToDelete?.id === route.id}
                                            onClose={() => {
                                                setShowDeleteConfirmation(false);
                                                setRouteToDelete(null);
                                            }}
                                            onConfirm={handleDeleteConfirm}
                                            isDeleting={isDeleting}
                                        />

                                        {/* Edit Form - Inside the route mapping */}
                                        {showEditForm === route.id && (
                                            <ThemedCard className="mt-4" onClick={(e) => e.stopPropagation()}>
                                                <ThemedCard.Header>
                                                    {t('editRoute')}
                                                </ThemedCard.Header>
                                                <ThemedCard.Body>
                                                {/* Button to switch to advanced mode if hidden waypoints exist */}
                                                {restrictEditingInNormalMode && (
                                                    <div className={cn(
                                                        "p-3 rounded mb-4 border",
                                                        "bg-yellow-50 dark:bg-yellow-900/20",
                                                        "text-yellow-800 dark:text-yellow-200",
                                                        "border-yellow-300 dark:border-yellow-700"
                                                    )}>
                                                        <AlertCircle className="inline mr-2" size={16} />
                                                        {t('restrictedEditingWarning')}
                                                        <button 
                                                        className={cn("ml-2 underline", text.link, text.linkHover)} 
                                                        onClick={() => setAdvancedMode(true)}
                                                        >
                                                        {t('switchToAdvancedMode')}
                                                        </button>
                                                    </div>
                                                )}
                                                
                                                <form onSubmit={handleSubmit}>
                                                    <div className="mb-4" onClick={(e) => e.stopPropagation()}>
                                                        <MultiLanguageField
                                                            entityId={currentEntity?.entityId}
                                                            entityDefaultLanguage={entitySettings?.entity_default_language || 'en'}
                                                            multiLanguageEnabled={entitySettings?.multi_language_enabled || false}
                                                            contentType="route"
                                                            contentId={route.id} // existing route ID for edit
                                                            fieldName="name"
                                                            defaultValue={newRoute.name}
                                                            onChange={handleTranslationChange}
                                                            label={t('routeName')}
                                                            required={true}
                                                            multiline={false}
                                                            disabled={false}
                                                        />
                                                    </div>
                                                    <div className="mb-4" onClick={(e) => e.stopPropagation()}>
                                                        <MultiLanguageField
                                                            entityId={currentEntity?.entityId}
                                                            entityDefaultLanguage={entitySettings?.entity_default_language || 'en'}
                                                            multiLanguageEnabled={entitySettings?.multi_language_enabled || false}
                                                            contentType="route"
                                                            contentId={route.id} // existing route ID for edit
                                                            fieldName="description"
                                                            defaultValue={newRoute.description || ''}
                                                            onChange={handleTranslationChange}
                                                            label={t('description')}
                                                            required={false}
                                                            multiline={true}
                                                            disabled={false}
                                                        />
                                                    </div>
                                                    {/* Waypoint/Stop Cards - Edit Mode */}
                                                    {(advancedMode ? selectedRoute.waypoints : selectedRoute.waypoints.filter(w => w.point_type === 'stop'))
                                                        .sort((a, b) => a.sequence_number - b.sequence_number)
                                                        .map((waypoint, index, orderedWaypoints) => {
                                                            // Calculate if stop can be removed (complex logic for edit mode)
                                                            const canRemove = waypoint.point_type !== 'stop' ||
                                                                (selectedRoute.waypoints.filter(w => w.point_type === 'stop').length > 2 &&
                                                                (
                                                                    (index === 0 && selectedRoute.waypoints[1]?.point_type === 'stop') ||
                                                                    (index === orderedWaypoints.length - 1 &&
                                                                    selectedRoute.waypoints[selectedRoute.waypoints.length - 2]?.point_type === 'stop') ||
                                                                    (index !== 0 && index !== orderedWaypoints.length - 1)
                                                                ));

                                                            return (
                                                                <WaypointFormCard
                                                                    key={waypoint.id}
                                                                    waypoint={waypoint}
                                                                    index={index}
                                                                    isFirst={index === 0}
                                                                    isLast={index === orderedWaypoints.length - 1}
                                                                    totalCount={orderedWaypoints.length}
                                                                    mode="edit"
                                                                    advancedMode={advancedMode}
                                                                    restrictedEditing={restrictEditingInNormalMode}
                                                                    onStopChange={handleStopChange}
                                                                    onMoveUp={moveStopUp}
                                                                    onMoveDown={moveStopDown}
                                                                    onRemove={handleRemoveStop}
                                                                    onPickLocation={handlePickStop}
                                                                    onAddressBlur={handleAddressBlur}
                                                                    pickingMode={pickingMode}
                                                                    currentStopIndex={currentStopIndex}
                                                                    onCancelPicking={() => setPickingMode(null)}
                                                                    boardingImagePreview={boardingImagePreviews[index]}
                                                                    boardingImage={boardingImages[index]}
                                                                    onImageSelect={handleBoardingImageSelect}
                                                                    onImageUpload={handleBoardingImageUpload}
                                                                    onImageDelete={handleBoardingImageDelete}
                                                                    onClearImageSelection={clearBoardingImageSelection}
                                                                    uploadingImage={uploadingImages[index]}
                                                                    entityId={currentEntity?.entityId}
                                                                    entitySettings={entitySettings}
                                                                    canRemoveStop={canRemove}
                                                                />
                                                            );
                                                        })}

                                                    <ThemedButton 
                                                        type="button" 
                                                        variant="secondary"
                                                        className="mb-2"
                                                        onClick={handleAddStop}
                                                        disabled={restrictEditingInNormalMode}
                                                    >
                                                        {t('addStop')}
                                                    </ThemedButton>
                                                    <ThemedButton 
                                                        type="submit" 
                                                        className="w-full mb-2"
                                                    >
                                                        {t('updateShuttleRoute')}
                                                    </ThemedButton>
                                                    <ThemedButton 
                                                        type="button" 
                                                        variant="secondary"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleCancelForm();
                                                        }}
                                                    >
                                                        {t('cancel')}
                                                    </ThemedButton>
                                                </form>
                                                </ThemedCard.Body>
                                            </ThemedCard>
                                        )}

                                    </div>
                                )}
                                </ThemedCard.Body>
                            </ThemedCard>
                        ); 
                    })}
                </div>                
            </div>
            
            {/* Right side: Map */}
            
            <div className="md:w-3/5 w-full relative z-10 h-[50vh] md:h-full">                                 
                <MapWithTooltips 
                    ref={mapControlRef}
                    selectedRoute={{
                        ...selectedRoute,
                        waypoints: selectedRoute ? 
                            (advancedMode ? 
                                // In advanced mode, show all waypoints
                                selectedRoute.waypoints?.sort((a, b) => a.sequence_number - b.sequence_number) : 
                                // In normal mode, show only stops
                                selectedRoute.waypoints
                                    ?.filter(wp => wp.point_type === 'stop')
                                    ?.sort((a, b) => a.sequence_number - b.sequence_number)
                            ) || [] : []
                    }}
                    mapCenter={mapCenter}
                    currentEntity={currentEntity}
                    showDefaultMarker={showDefaultMarker}
                    entityAddress={entityAddress}
                    entityCoordinates={[
                        currentEntity.latitude ? parseFloat(currentEntity.latitude) : 43.7921,
                        currentEntity.longitude ? parseFloat(currentEntity.longitude) : -79.3552
                    ]}
                    t={t}
                    pickingMode={pickingMode}                    
                    onLocationPick={handleLocationPick}
                    onMapChange={handleMapChange}
                    entityChangeRef={entityChangeRef}
                    isDriver={currentEntity?.userRole !== 'user'}
                    showTooltips={showTooltips}
                    hasRouteReferenceLocations={hasRouteReferenceLocations}                                       
                > 
                    {routeReferenceLocations && routeReferenceLocations.length > 1 && (
                        <Polyline
                            positions={routeReferenceLocations}
                            pane="markerPane"
                            pathOptions={{
                                color: '#3B82F6',
                                weight: highlightedSegmentKey === 'sequential-route' ? 6 : 4,
                                opacity: highlightedSegmentKey && highlightedSegmentKey !== 'sequential-route' ? 0.4 : 0.8
                            }}
                            eventHandlers={{
                                mouseover: () => setHighlightedSegmentKey('sequential-route'),
                                mouseout: () => setHighlightedSegmentKey(null)
                            }}
                        >
                            <Tooltip sticky>{selectedRoute?.name || t('route')}</Tooltip>
                        </Polyline>
                    )}
                    {advancedMode && availableRouteTypes.length > 0 && (
                        <div className={cn(
                            "absolute left-2 bottom-2 z-[1000] rounded-md px-2 py-1 text-xs shadow",
                            components.card.background,
                            text.primary
                        )}>
                            {availableRouteTypes.length === 1 ? (
                                // Single type — show as static label, no dropdown needed
                                <span>{availableRouteTypes[0].displayName}</span>
                            ) : (
                                // Multiple types — show dropdown selector
                                <select
                                    value={segmentPolylinesArchivedType || ''}
                                    onChange={(e) => handleSegmentTypeChange(e.target.value)}
                                    className={cn(
                                        "text-xs rounded border px-1 py-0.5 cursor-pointer",
                                        "bg-transparent border-gray-300 dark:border-gray-600",
                                        text.primary
                                    )}
                                >
                                    {availableRouteTypes.map(type => (
                                        <option key={type.typeKey} value={type.typeKey}>
                                            {type.displayName}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                    )}
                    {advancedMode && (
                        <div className={cn(
                            "absolute right-2 bottom-2 z-[1000] w-[360px] rounded-md border px-3 py-2 text-xs shadow",
                            components.card.background,
                            "border-gray-300 dark:border-gray-600",
                            text.primary
                        )}>
                            <div className="font-semibold">Segment Debug</div>
                            {highlightedSegmentDebug ? (
                                <div className="mt-1 space-y-1">
                                    <div>
                                        {highlightedSegmentDebug.segment.fromWaypointName} {'->'} {highlightedSegmentDebug.segment.toWaypointName}
                                    </div>
                                    <div>Source: {highlightedSegmentDebug.sourceHint}</div>
                                    <div>
                                        Variant: {highlightedSegmentDebug.segment.variant || 'standard'} | Points: {highlightedSegmentDebug.segment.pointCount || highlightedSegmentDebug.segment.polyline?.length || 0}
                                    </div>
                                    <div>
                                        Seq gap: {highlightedSegmentDebug.sequenceGap ?? 'n/a'} | Distance: {formatOptionalDistanceFromMeters(highlightedSegmentDebug.segment.totalDistanceM)}
                                    </div>
                                    <div>
                                        Straight line: {formatOptionalDistanceFromMeters(highlightedSegmentDebug.straightLineDistanceM)}
                                        {' '}| Ratio: {Number.isFinite(highlightedSegmentDebug.routeToStraightRatio) ? highlightedSegmentDebug.routeToStraightRatio.toFixed(2) : 'n/a'}x
                                    </div>
                                    <div>
                                        Snap start/end: {formatOptionalDistanceFromMeters(highlightedSegmentDebug.startSnapOffsetM)} / {formatOptionalDistanceFromMeters(highlightedSegmentDebug.endSnapOffsetM)}
                                    </div>
                                    <div>
                                        From wp: {formatCoord(highlightedSegmentDebug.fromCoord?.lat)}, {formatCoord(highlightedSegmentDebug.fromCoord?.lng)}
                                    </div>
                                    <div>
                                        To wp: {formatCoord(highlightedSegmentDebug.toCoord?.lat)}, {formatCoord(highlightedSegmentDebug.toCoord?.lng)}
                                    </div>
                                    <div>
                                        First pt: {formatCoord(highlightedSegmentDebug.firstPoint?.lat)}, {formatCoord(highlightedSegmentDebug.firstPoint?.lng)}
                                    </div>
                                    <div>
                                        Last pt: {formatCoord(highlightedSegmentDebug.lastPoint?.lat)}, {formatCoord(highlightedSegmentDebug.lastPoint?.lng)}
                                    </div>
                                    <div>
                                        Time window: {formatIsoTimestamp(highlightedSegmentDebug.segment.firstTimestamp)} to {formatIsoTimestamp(highlightedSegmentDebug.segment.lastTimestamp)}
                                    </div>
                                </div>
                            ) : (
                                <div className={cn("mt-1", text.secondary)}>
                                    Hover a segment to inspect snap offsets, geometry source, and distance ratio.
                                </div>
                            )}
                        </div>
                    )}
                    {advancedMode && segmentPolylines.map((segment, index) => {
                        const segmentKey = getSegmentKey(segment);
                        const isHighlighted = highlightedSegmentKey === segmentKey;
                        const hasHighlightedSegment = Boolean(highlightedSegmentKey);
                        const color = SEGMENT_COLORS[index % SEGMENT_COLORS.length];
                        const distanceLabel = formatOptionalDistanceFromMeters(segment.totalDistanceM);
                        const tooltipText = segment.variant
                            ? t('segmentPolylineTooltipWithVariant', {
                                from: segment.fromWaypointName,
                                to: segment.toWaypointName,
                                variant: segment.variant,
                                distance: distanceLabel
                            })
                            : t('segmentPolylineTooltip', {
                                from: segment.fromWaypointName,
                                to: segment.toWaypointName,
                                distance: distanceLabel
                            });

                        return (
                            // Render in markerPane so segments share the same Canvas as
                            // waypoint CircleMarkers. The markerPane Canvas (z-index 600)
                            // blocks all mouse events from reaching the overlayPane Canvas
                            // (z-index 400), so polylines in overlayPane never get hover.
                            <Polyline
                                key={`segment-${segmentKey}`}
                                positions={segment.polyline}
                                pane="markerPane"
                                pathOptions={{
                                    color,
                                    weight: isHighlighted ? 6 : 4,
                                    opacity: hasHighlightedSegment ? (isHighlighted ? 1 : 0.25) : 0.85,
                                    dashArray: '8 6'
                                }}
                                interactive={true}
                                eventHandlers={{
                                    mouseover: () => setHighlightedSegmentKey(segmentKey),
                                    mouseout: () => setHighlightedSegmentKey(null)
                                }}
                            >
                                <Tooltip sticky>{tooltipText}</Tooltip>
                            </Polyline>
                        );
                    })}
                    {advancedMode && highlightedSegmentKey && (() => {
                        const highlightedIndex = segmentPolylines.findIndex(
                            segment => getSegmentKey(segment) === highlightedSegmentKey
                        );
                        if (highlightedIndex < 0) {
                            return null;
                        }
                        const highlightedSegment = segmentPolylines[highlightedIndex];

                        const highlightedColor = SEGMENT_COLORS[
                            highlightedIndex % SEGMENT_COLORS.length
                        ];

                        return getDecimatedBreadcrumbPoints(highlightedSegment.polyline).map((point, pointIndex) => (
                            <CircleMarker
                                key={`breadcrumb-${highlightedSegmentKey}-${pointIndex}`}
                                center={point}
                                radius={4}
                                pane="markerPane"
                                pathOptions={{
                                    color: '#FFFFFF',
                                    weight: 1.5,
                                    fillColor: highlightedColor,
                                    fillOpacity: 0.95
                                }}
                                interactive={false}
                            />
                        ));
                    })()}
                    <MapControls     
                        mapRef={mapControlRef}
                        showTooltips={showTooltips}                        
                        onToggleTooltips={setShowTooltips}
                        t={t}
                    />
                </MapWithTooltips>
            </div>

            {/* Segment Matrix Modal */}
            <SegmentMatrixModal
                isOpen={showSegmentMatrixModal}
                onClose={() => {
                    // Clear cached segments for this route (in case user recalculated)
                    if (segmentMatrixRoute?.id) {
                        setRouteSegments(prev => {
                            const updated = { ...prev };
                            delete updated[segmentMatrixRoute.id];
                            return updated;
                        });
                        // Re-fetch segments if route is still selected
                        fetchRouteSegments(segmentMatrixRoute.id);
                    }
                    setShowSegmentMatrixModal(false);
                    setSegmentMatrixRoute(null);
                }}
                entityId={currentEntity?.entityId}
                routeId={segmentMatrixRoute?.id}
                routeName={getRouteDisplayName(segmentMatrixRoute)}
                waypoints={segmentMatrixRoute?.waypoints || []}
            />
        </div>
    );
};

export default RouteManagement;
    
